import {
  ConnectionListResult,
  FeatureFlagsContext,
  GridSortProps,
  PaginatedQueryWrapper,
  SelectionProviderChildProps,
} from '@tableau/data-catalog-shared';
import * as React from 'react';
import {
  WorkbookFieldsByIdQuery,
  WorkbookFieldsByIdQuery_workbooks_embeddedDatasources_fieldsConnection_nodes,
  WorkbookFieldsByIdQuery_workbooks_embeddedDatasources_fieldsConnection_nodes_ColumnField,
  WorkbookFieldsByIdQueryVariables,
} from '../../../gen-src/queries/WorkbookFieldsByIdQuery';
import { FieldSortOrder } from '../../../gen-src/queries/globalTypes';
import { WorkbookFieldsByIdQueryGql } from '../../queries/WorkbookFieldsByIdQueryGql';
import { WorkbookFieldsWithInheritanceByIdQueryGql } from '../../queries/WorkbookFieldsWithInheritanceByIdQueryGql';
import { iconForField } from '../field/functions/FieldFunctions';
import { FieldsValidSortFields } from '../sorting/sortProviders/FieldsGridSortProvider';
import { WorkbookFieldsDisplayGrid, WorkbookFieldsDisplayGridRowData } from './WorkbookFieldsDisplayGrid';

export type QueryVariables = WorkbookFieldsByIdQueryVariables;
export type QueryResults = WorkbookFieldsByIdQuery;
export type QueryResultListNode = WorkbookFieldsByIdQuery_workbooks_embeddedDatasources_fieldsConnection_nodes;
export type GridRowData = WorkbookFieldsDisplayGridRowData;

export type WorkbookFieldsQueryGridProps =
  SelectionProviderChildProps &
  GridSortProps<FieldsValidSortFields> & {
    readonly vizportalUrlId: string;
    readonly selectedEmbeddedDatasourceId: string;
    readonly rowCount: number | null;
  };

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.workbooks && resultData.workbooks[0] &&
    resultData.workbooks[0].embeddedDatasources[0] && resultData.workbooks[0].embeddedDatasources[0].fieldsConnection;
}

export function mapResultNode(field: QueryResultListNode, resultData: QueryResults): GridRowData {
  return {
    ...field,
    dataType: (field as WorkbookFieldsByIdQuery_workbooks_embeddedDatasources_fieldsConnection_nodes_ColumnField).dataType || null,
    workbookId: resultData.workbooks[0].vizportalUrlId,
    fieldIcon: iconForField(field),
    sheetCount: field.downstreamSheetsConnection ? field.downstreamSheetsConnection.totalCount : null,
  };
}

export function WorkbookFieldsQueryGrid(props: WorkbookFieldsQueryGridProps): JSX.Element {
  const queryGqlClass =
    React.useContext(FeatureFlagsContext).fieldDescriptionInheritanceLineageEnabled
      ? WorkbookFieldsWithInheritanceByIdQueryGql
      : WorkbookFieldsByIdQueryGql;

  return (
    <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
      queryGql={queryGqlClass}
      partialQueryVariables={{
        workbookVizportalUrlId: props.vizportalUrlId,
        selectedEmbeddedDatasourceId: props.selectedEmbeddedDatasourceId,
        orderBy: props.orderBy as FieldSortOrder,
      }}
      selectConnectionListResult={selectConnectionListResult}
      mapResultNode={mapResultNode}
    >
      {({ rowStore }) => (
        <WorkbookFieldsDisplayGrid
          rowStore={rowStore}
          selectedKeys={props.selectedKeys}
          updateSelectionForKey={props.updateSelectionForKey}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />
      )}
    </PaginatedQueryWrapper>
  );
}
